import { Link } from "react-router-dom";
import { getSpecificationLabel, humanoidRobots } from "../../data/humanoidCatalog";

const known = (value) => Number.isFinite(value);

export default function CatalogModelComparison({ robot }) {
  const candidates = humanoidRobots.filter((item) =>
    item.id !== robot.id && item.category === robot.category && item.indexable !== false &&
    (known(robot.heightCm) ? known(item.heightCm) : item.manufacturerId === robot.manufacturerId),
  ).sort((a, b) => {
    const distance = (item) => known(robot.heightCm)
      ? Math.abs(item.heightCm - robot.heightCm)
      : 0;
    return distance(a) - distance(b) || a.name.localeCompare(b.name);
  }).slice(0, 2);
  if (!candidates.length) return null;
  const records = [robot, ...candidates];
  const comparedFields = [
    ["Height", "heightCm"],
    ["Mass", "weightKg"],
    ["Recorded degrees of freedom", "degreesOfFreedom"],
  ];
  const deltas = candidates.flatMap((item) => {
    const values = [];
    if (known(robot.heightCm) && known(item.heightCm)) {
      const delta = Math.round((robot.heightCm - item.heightCm) * 10) / 10;
      values.push(delta === 0
        ? `${robot.name} and ${item.name} have the same recorded height of ${robot.heightCm} cm.`
        : `${robot.name} is recorded as ${Math.abs(delta)} cm ${delta > 0 ? "taller" : "shorter"} than ${item.name}.`);
    }
    return values;
  });
  return (
    <section className="site-shell py-12" aria-labelledby="catalog-comparison-title">
      <h2 id="catalog-comparison-title" className="text-3xl font-black">Compare the recorded dimensions of {robot.name}</h2>
      <p className="mt-4 max-w-4xl text-sm leading-7 text-neutral-700">
        {known(robot.heightCm)
          ? "The two comparison records have the nearest recorded heights in the same catalog category."
          : "These comparison records belong to the same manufacturer and catalog category."}
      </p>
      <div className="mt-6 overflow-x-auto rounded-2xl border border-neutral-200">
        <table className="w-full min-w-[660px] text-left text-sm">
          <caption className="bg-neutral-50 px-5 py-3 text-left">Catalog records with their original source dates</caption>
          <thead className="bg-neutral-950 text-white"><tr><th scope="col" className="px-5 py-4">Field</th>{records.map((item) => <th scope="col" key={item.id} className="px-5 py-4"><Link to={`/humanoid-robots/${item.slug}`} className="underline">{item.name}</Link></th>)}</tr></thead>
          <tbody className="divide-y divide-neutral-200">
            {comparedFields.map(([label, field]) => <tr key={field}><th scope="row" className="px-5 py-4">{label}</th>{records.map((item) => <td className="px-5 py-4" key={item.id}>{getSpecificationLabel(item, field)}</td>)}</tr>)}
            <tr><th scope="row" className="px-5 py-4">Source date in record</th>{records.map((item) => <td key={item.id} className="px-5 py-4">{item.lastVerifiedAt || "Not recorded"}</td>)}</tr>
            <tr><th scope="row" className="px-5 py-4">Product source</th>{records.map((item) => <td key={item.id} className="px-5 py-4"><a href={item.officialProductUrl} target="_blank" rel="noreferrer" className="font-bold text-blue-700 underline">{item.name} documentation</a></td>)}</tr>
          </tbody>
        </table>
      </div>
      {deltas.map((text) => <p key={text} className="mt-4 text-sm leading-7 text-neutral-700">{text} This subtraction uses the recorded heights.</p>)}
      <p className="mt-4 max-w-4xl text-sm leading-7 text-neutral-600">Dimensions do not measure task success. Joint counts may cover different configurations or include different hand mechanisms. Confirm each configuration in its linked documentation before comparing control access.</p>
    </section>
  );
}
